import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';

import { Observable, throwError, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';

@Injectable()
export class LoginService {

    private host: any = {};

    constructor(private client: HttpClient) {
        this.host['v2-accounts'] = environment.host('v2-accounts');
        this.host['v1-accounts'] = environment.host('v1-accounts');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('lastRefresh');
    }

    get accessTokenV2(): string | null {
        return localStorage.getItem('accessTokenV2');
    }

    set accessTokenV2(value: string | null) {
        if (value) {
            localStorage.setItem('accessTokenV2', value);
        } else {
            localStorage.removeItem('accessTokenV2');
        }
    }

    get accessTokenV1(): string | null {
        return localStorage.getItem('accessTokenV1');
    }

    set accessTokenV1(value: string | null) {
        if (value) {
            localStorage.setItem('accessTokenV1', value);
        } else {
            localStorage.removeItem('accessTokenV1');
        }
    }

    get refreshTokenV2(): string | null {
        return localStorage.getItem('refreshTokenV2');
    }

    set refreshTokenV2(value: string | null) {
        if (value) {
            localStorage.setItem('refreshTokenV2', value);
        } else {
            localStorage.removeItem('refreshTokenV2');
        }
    }

    get refreshTokenV1(): string | null {
        return localStorage.getItem('refreshTokenV1');
    }

    set refreshTokenV1(value: string | null) {
        if (value) {
            localStorage.setItem('refreshTokenV1', value);
        } else {
            localStorage.removeItem('refreshTokenV1');
        }
    }

    get lastRefreshTimeV2(): number {
        return Number.parseInt(String(localStorage.getItem('lastRefreshTimeV2')));
    }

    set lastRefreshTimeV2(value: number) {
        if (value) {
            localStorage.setItem('lastRefreshTimeV2', value.toString());
        } else {
            localStorage.removeItem('lastRefreshTimeV2');
        }
    }

    get lastRefreshTimeV1(): number {
        return Number.parseInt(String(localStorage.getItem('lastRefreshTimeV1')));
    }

    set lastRefreshTimeV1(value: number) {
        if (value) {
            localStorage.setItem('lastRefreshTimeV1', value.toString());
        } else {
            localStorage.removeItem('lastRefreshTimeV1');
        }
    }

    readonly sessionExpiredSubject: Subject<boolean> = new Subject<boolean>();

    logout(): void {
        this.accessTokenV2 = null;
        this.accessTokenV1 = null;
        this.refreshTokenV2 = null;
        this.refreshTokenV1 = null;
        this.lastRefreshTimeV2 = NaN;
        this.lastRefreshTimeV1 = NaN;
    }

    login(username: string, password: string): Observable<any> {
        return this.client.request('POST', `${this.host['v2-accounts']}/v2/accounts/token?grant_type=password`, {
            observe: 'response',
            body: {
                client_id: environment.clientId,
                grant_type: 'password',
                username: username,
                password: password
            }
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.login.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.login.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    whoami(accessToken: string): Observable<any> {
        return this.client.request('GET', `${this.host['v2-accounts']}/v2/accounts/whoami`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.whoami.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.whoami.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    v1Auth(accountId: string, accessToken: string): Observable<any> {
        return this.client.request('POST', `${this.host['v2-accounts']}/v2/accounts/${accountId}/v1/auth`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.v1Auth.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.v1Auth.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getJwtByToken(token: string): Observable<any> {
        return this.client.request('GET', `${this.host['v1-accounts']}/v1/jwt`, {
            headers: { 'Authorization': `Bearer ${token}` },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getJwtByToken.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getJwtByToken.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    requestExtendedRefreshTokenV2(refreshToken: string | null): Observable<any> {
        return this.client.request('POST', `${this.host['v2-accounts']}/v2/accounts/token?grant_type=refresh_token`, {
            observe: 'response',
            body: {
                client_id: environment.clientId,
                grant_type: 'refresh_token',
                refresh_token: refreshToken
            }
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.requestExtendedRefreshTokenV2.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.requestExtendedRefreshTokenV2.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    requestExtendedRefreshTokenV1(refreshToken: string | null): Observable<any> {
        return this.client.request('GET', `${this.host['v1-accounts']}/v1/jwt/extended_refresh`, {
            headers: { 'Authorization': `Bearer ${refreshToken}` },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.requestExtendedRefreshTokenV1.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.requestExtendedRefreshTokenV1.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getTokenByAccountIdV2(accountId: string): Observable<any> {
        return this.client.request('POST', `${this.host['v2-accounts']}/v2/accounts/${accountId}/token`, {
            headers: { 'X-MyKronoz-Authorization': 'v2;admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getTokenByAccountIdV2.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getTokenByAccountIdV2.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getTokenByAccountIdV1(accountId: string): Observable<any> {
        return this.client.request('GET', `${this.host['v1-accounts']}/v1/jwt/auth/uid/${accountId}`, {
            headers: { 'X-MyKronoz-Authorization': 'admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getTokenByAccountIdV1.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getTokenByAccountIdV1.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getTokenByEmailV2(email: string): Observable<any> {
        return this.client.request('POST', `${this.host['v2-accounts']}/v2/accounts/${email}/token`, {
            headers: { 'X-MyKronoz-Authorization': 'v2;admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getTokenByEmailV2.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getTokenByEmailV2.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getTokenByEmailV1(email: string): Observable<any> {
        return this.client.request('GET', `${this.host['v1-accounts']}/v1/jwt/auth/${email}`, {
            headers: { 'X-MyKronoz-Authorization': 'admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getTokenByEmailV1.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getTokenByEmailV1.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getWhoamiV2(): Observable<any> {
        return this.client.request('GET', `${this.host['v2-accounts']}/v2/accounts/whoami`, {
            headers: { 'X-MyKronoz-Authorization': 'v2;admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getWhoamiV2.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getWhoamiV2.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getAccountByAccountIdV2(accountId: string): Observable<any> {
        return this.client.request('GET', `${this.host['v2-accounts']}/v2/accounts/${accountId}`, {
            headers: { 'X-MyKronoz-Authorization': 'v2;admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getAccountByAccountIdV2.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getAccountByAccountIdV2.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getProfileByAccountIdV2(accountId: string): Observable<any> {
        return this.client.request('GET', `${this.host['v2-accounts']}/v2/accounts/${accountId}/profile`, {
            headers: { 'X-MyKronoz-Authorization': 'v2;admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getProfileByAccountIdV2.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getProfileByAccountIdV2.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getJwtV1(): Observable<any> {
        return this.client.request('GET', `${this.host['v1-accounts']}/v1/jwt`, {
            headers: { 'X-MyKronoz-Authorization': 'admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getJwtV1.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getJwtV1.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }

    getAccountByAccountIdV1(accountId: string): Observable<any> {
        return this.client.request('GET', `${this.host['v1-accounts']}/v1/jwt/account/uid/${accountId}`, {
            headers: { 'X-MyKronoz-Authorization': 'admin' },
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<any>) => {
                console.debug('LoginService.getAccountByAccountIdV1.map', response);
                return response.body;
            }),
            catchError((response: HttpErrorResponse) => {
                console.debug('LoginService.getAccountByAccountIdV1.catchError', response);
                if (response.error instanceof ErrorEvent) {
                } else {
                }
                return throwError(() => ({ status: response.status }));
            })
        );
    }
}
